import type * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import * as transformers from '@attain-apps/transformers'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import gql from 'graphql-tag'

import * as common from '../../../../../common'
import * as meGql from '../../../../gql'

const DOC = gql`
  ${common.gql.dms.data.fragments.variableValue.DOC}
  ${common.gql.dms.data.fragments.variable.DOC}
  ${meGql.fragments.agentForUser.DOC}
  query MeZoneApps($tenantId: ID!, $zoneId: ID!) {
    me {
      tenant(id: $tenantId) {
        zone(id: $zoneId) {
          apps {
            access
            info {
              id
              alias
              name
              icon
              description
              createdBy
              supportsScenarios
              supportsFunctionalityConfiguration
              scenarioVariables {
                ...FragmentVariable
              }
              sessionVariables {
                ...FragmentVariable
              }
            }
            pages {
              id
              alias
              pageId
              name
              description
              display {
                isMain
                position
              }
              variables {
                ...FragmentVariableValue
              }
            }
            scenarios {
              access
              info {
                id
                alias
                name
                description
                createdBy
                visibility
                period {
                  startDate
                  endDate
                }
                variables {
                  ...FragmentVariableValue
                }
              }
            }
            agents {
              ...FragmentMeAgentForUser
            }
          }
        }
      }
    }
  }
`

// A missing tenant or zone must fail rather than look like an empty app list.
const TRANSFORMER = graphql.transformers.OUT.validate(
  graphql.transformers.OUT.select<
    graphql.schema.MeZoneAppsQuery,
    readonly ents.core.admin.App.ForUser[],
    'me'
  >(
    'me',
    graphql.transformers.OUT.select(
      'tenant',
      graphql.transformers.OUT.select(
        'zone',
        graphql.transformers.OUT.select(
          'apps',
          graphql.transformers.OUT.default(
            graphql.transformers.OUT.list(
              graphql.transformers.OUT.object({
                access: meGql.transformers.passThroughAndValidateNotNull(),
                agents: meGql.fragments.agentForUser.LIST_TRANSFORMER,
                alias:
                  meGql.transformers.extractFromInfoAndValidateNotNull('alias'),
                createdBy: meGql.transformers.extractFromInfo('createdBy'),
                description: meGql.transformers.extractFromInfo('description'),
                icon: meGql.transformers.extractFromInfoAndValidateNotNull(
                  'icon',
                ),
                id: meGql.transformers.extractFromInfoAndValidateNotNull('id'),
                name: meGql.transformers.extractFromInfoAndValidateNotNull(
                  'name',
                ),
                pages: graphql.transformers.OUT.default(
                  graphql.transformers.OUT.list(
                    graphql.transformers.OUT.object({
                      alias: meGql.transformers.passThroughAndValidateNotNull(),
                      description: transformers.PASS_THROUGH,
                      display: graphql.transformers.OUT.object({
                        isMain: graphql.transformers.OUT.default(
                          transformers.PASS_THROUGH,
                          false,
                        ),
                        position: graphql.transformers.OUT.validate(
                          transformers.PASS_THROUGH,
                          TypeChecker.isNotNullish,
                          { warnOnly: true },
                        ),
                      }),
                      id: meGql.transformers.passThroughAndValidateNotNull(),
                      name: meGql.transformers.passThroughAndValidateNotNull(),
                      pageId:
                        meGql.transformers.passThroughAndValidateNotNull(),
                      variables:
                        common.gql.dms.data.fragments.variableValue
                          .LOOKUP_TRANSFORMER,
                    }),
                    { onNullish: 'remove' },
                  ),
                  [],
                ),
                scenarioVariables: {
                  usingFullInput: graphql.transformers.OUT.default(
                    graphql.transformers.OUT.select(
                      'info',
                      graphql.transformers.OUT.select(
                        'scenarioVariables',
                        graphql.transformers.OUT.list(
                          common.gql.dms.data.fragments.variable.TRANSFORMER,
                          { onNullish: 'remove' },
                        ),
                      ),
                    ),
                    [],
                  ),
                },
                scenarios: graphql.transformers.OUT.default(
                  graphql.transformers.OUT.list(
                    graphql.transformers.OUT.object({
                      access:
                        meGql.transformers.passThroughAndValidateNotNull(),
                      alias:
                        meGql.transformers.extractFromInfoAndValidateNotNull(
                          'alias',
                        ),
                      createdBy:
                        meGql.transformers.extractFromInfo('createdBy'),
                      description:
                        meGql.transformers.extractFromInfo('description'),
                      id: meGql.transformers.extractFromInfoAndValidateNotNull(
                        'id',
                      ),
                      name: meGql.transformers.extractFromInfoAndValidateNotNull(
                        'name',
                      ),
                      period: {
                        usingFullInput: graphql.transformers.OUT.default(
                          graphql.transformers.OUT.select(
                            'info',
                            graphql.transformers.OUT.select(
                              'period',
                              graphql.transformers.OUT.object({
                                end: {
                                  usingFullInput:
                                    graphql.transformers.OUT.select(
                                      'endDate',
                                      transformers.PASS_THROUGH,
                                    ),
                                },
                                includesEnd: () => true,
                                includesStart: () => true,
                                start: {
                                  usingFullInput:
                                    graphql.transformers.OUT.select(
                                      'startDate',
                                      transformers.PASS_THROUGH,
                                    ),
                                },
                              }),
                            ),
                          ),
                          {},
                        ),
                      },
                      variables: {
                        usingFullInput: graphql.transformers.OUT.default(
                          graphql.transformers.OUT.select(
                            'info',
                            graphql.transformers.OUT.select(
                              'variables',
                              common.gql.dms.data.fragments.variableValue
                                .LOOKUP_TRANSFORMER,
                            ),
                          ),
                          {},
                        ),
                      },
                      visibility: {
                        usingFullInput: graphql.transformers.OUT.default(
                          graphql.transformers.OUT.select(
                            'info',
                            graphql.transformers.OUT.select(
                              'visibility',
                              common.gql.admin.scenario.enums.scenarioVisibility
                                .TRANSFORMER,
                            ),
                          ),
                          2 satisfies ents.core.admin.Scenario.Visibility,
                        ),
                      },
                    }),
                    { onNullish: 'remove' },
                  ),
                  [],
                ),
                sessionVariables: {
                  usingFullInput: graphql.transformers.OUT.default(
                    graphql.transformers.OUT.select(
                      'info',
                      graphql.transformers.OUT.select(
                        'sessionVariables',
                        graphql.transformers.OUT.list(
                          common.gql.dms.data.fragments.variable.TRANSFORMER,
                          { onNullish: 'remove' },
                        ),
                      ),
                    ),
                    [],
                  ),
                },
                supportsScenarios:
                  meGql.transformers.extractFromInfoWithDefault(
                    'supportsScenarios',
                    false,
                  ),
                supportsFunctionalityConfiguration:
                  meGql.transformers.extractFromInfo(
                    'supportsFunctionalityConfiguration',
                  ),
              }),
              { onNullish: 'remove' },
            ),
            [],
          ),
        ),
      ),
    ),
  ),
  TypeAsserter.assertIsNotNullish,
)

export const execute = graphql.createExecutor<
  graphql.schema.MeZoneAppsQuery,
  readonly ents.core.admin.App.ForUser[],
  graphql.schema.MeZoneAppsQueryVariables
>({
  query: DOC,
  transformer: TRANSFORMER,
})
